import { MutationFunctionOptions } from '@apollo/client';
import {
  UpdateCampaignResponse,
  UpdateCampaignVariables,
  getUpdateCampaignMutationPayload,
  useUpdateCampaignMutation,
} from 'modules/campaigns/editor/hooks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useCampaignQueryContext } from '../campaignFolders/contexts/CampaignQueryContext';

export const useDisableCampaignAlerts = (
  { campaignId, campaignName }: { campaignId: number; campaignName: string },
  options: Omit<
    MutationFunctionOptions<UpdateCampaignResponse, UpdateCampaignVariables>,
    'variables'
  > = {},
) => {
  const { updateCampaign, data, error, isLoading } = useUpdateCampaignMutation();
  const { refetch } = useCampaignQueryContext();

  const dispatch = useDispatch();

  const disableAlerts = useCallback(() => {
    const variables = getUpdateCampaignMutationPayload(campaignId, {
      campaignAlertsEnabled: false,
    });

    updateCampaign({
      ...options,
      variables,
      onCompleted: (_data, clientOptions) => {
        dispatch(addPositiveToast(`Disabled alerts for ${campaignName}`));

        refetch();

        options?.onCompleted?.(_data, clientOptions);
      },
      onError: (e, clientOptions) => {
        const errorMessage = `Unable to disable campaign alerts. Please try disabling at the campaign or try again.`;
        dispatch(addNegativeToastWithErrorId(errorMessage, 'useDisableCampaignAlerts', e));

        options?.onError?.(e, clientOptions);
      },
    });
  }, [updateCampaign, campaignId, campaignName, options, dispatch, refetch]);

  return {
    data,
    error,
    isLoading,
    disableAlerts,
  };
};
